import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { RefreshCw, Newspaper, Download, Copy, Check, Brain, FileText } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "./lib/backend";

const API = `${BACKEND_URL}/api`;

export function NewsletterModal({ selectedArticles, getSelectedArticleObjects, darkMode, disabled, onSend }) {
  const { authHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("config"); // config | generating | preview
  const [format, setFormat] = useState("digest");
  const [includeBrief, setIncludeBrief] = useState(false);
  const [newsletter, setNewsletter] = useState("");
  const [editableNewsletter, setEditableNewsletter] = useState("");
  const [copied, setCopied] = useState(false);
  const dm = darkMode;

  const handleGenerate = async () => {
    const articles = getSelectedArticleObjects();
    if (!articles.length) {
      toast.warning("Select articles first");
      return;
    }
    setStep("generating");
    // Auto-stage selected articles as brief priority
    if (onSend) onSend();
    try {
      const res = await axios.post(
        `${API}/admin/generate-newsletter`,
        { articles, format_type: format, include_brief: includeBrief },
        { headers: { ...authHeader(), "Content-Type": "application/json" } }
      );
      setNewsletter(res.data.newsletter);
      setEditableNewsletter(res.data.newsletter);
      setStep("preview");
    } catch (err) {
      toast.error("Generation failed", { description: err.response?.data?.detail || err.message });
      setStep("config");
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editableNewsletter);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadMd = () => {
    const blob = new Blob([editableNewsletter], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-${new Date().toISOString().split("T")[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded as .md");
  };

  const handleDownloadHtml = () => {
    // Convert basic markdown to HTML
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>TourismIntel Newsletter</title>
<style>
  body{max-width:680px;margin:40px auto;font-family:'Georgia',serif;color:#191c1d;line-height:1.7;padding:0 20px;}
  h1{font-size:28px;letter-spacing:-0.02em;margin-bottom:8px;}
  h2{font-size:20px;letter-spacing:-0.01em;margin-top:40px;}
  h3{font-size:16px;margin-top:28px;}
  p{margin:16px 0;}
  strong{font-weight:700;}
  em{font-style:italic;}
  hr{border:none;border-top:1px solid #f3f4f5;margin:32px 0;}
  .meta{font-family:'Arial',sans-serif;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:#6b7a7d;}
</style>
</head>
<body>
<p class="meta">TourismIntel · ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p>
${editableNewsletter
  .replace(/^# (.+)$/gm, '<h1>$1</h1>')
  .replace(/^## (.+)$/gm, '<h2>$1</h2>')
  .replace(/^### (.+)$/gm, '<h3>$1</h3>')
  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  .replace(/\*(.+?)\*/g, '<em>$1</em>')
  .replace(/^---$/gm, '<hr>')
  .replace(/\n\n/g, '</p><p>')
  .replace(/^(?!<[h|p|h])/gm, '')
}
</body>
</html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-${new Date().toISOString().split("T")[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded as .html");
  };

  const reset = () => { setStep("config"); setNewsletter(""); setEditableNewsletter(""); };

  const articleCount = selectedArticles.size;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button
          disabled={disabled || articleCount === 0}
          data-testid="newsletter-btn"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-40 ${
            articleCount > 0
              ? (dm ? "bg-violet-400/15 text-violet-300 hover:bg-violet-400/25" : "bg-violet-50 text-violet-700 hover:bg-violet-100")
              : (dm ? "bg-[#1a2122] text-slate-500" : "bg-[#f3f4f5] text-[#bac9cc]")
          }`}
        >
          <Newspaper className="w-3 h-3" />
          Newsletter ({articleCount})
        </button>
      </DialogTrigger>

      <DialogContent
        className={`max-w-3xl max-h-[90vh] flex flex-col border-0 ${dm ? "bg-[#0c1011] text-slate-100" : "bg-white text-[#191c1d]"}`}
        style={{ boxShadow: "0 0 60px rgba(0,0,0,0.18)" }}
      >
        <DialogHeader className="flex-shrink-0 pb-3">
          <DialogTitle className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
            Generate Newsletter
          </DialogTitle>
          <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            {articleCount} ARTICLES SELECTED · IN MIRKO LALLI&apos;S VOICE · CLAUDE AI
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">

          {/* ── STEP: CONFIG ── */}
          {step === "config" && (
            <div className="space-y-6 py-2">
              <div>
                <p className={`text-sm font-semibold mb-3 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Newsletter format</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: "digest", icon: FileText, title: "Curated Digest", desc: "400–600 words. Tight, punchy. 3-5 stories synthesized into one narrative thread." },
                    { id: "longform", icon: Brain, title: "Long-Form Article", desc: "1,200–2,500 words. Deep narrative arc, full camera flip, contrarian analysis." },
                  ].map(({ id, icon: Icon, title, desc }) => (
                    <button
                      key={id}
                      onClick={() => setFormat(id)}
                      className={`rounded-xl p-5 text-left transition-all border-2 ${
                        format === id
                          ? (dm ? "border-violet-400 bg-violet-400/10" : "border-violet-600 bg-violet-50")
                          : (dm ? "border-transparent bg-[#1a2122] hover:bg-[#1e2627]" : "border-transparent bg-[#f3f4f5] hover:bg-[#ecedef]")
                      }`}
                    >
                      <Icon className={`w-4 h-4 mb-3 ${format === id ? (dm ? "text-violet-400" : "text-violet-600") : (dm ? "text-slate-500" : "text-[#6b7a7d]")}`} />
                      <p className={`text-sm font-semibold mb-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{title}</p>
                      <p className={`text-xs leading-relaxed ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className={`rounded-xl p-4 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
                <p className={`text-xs leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  Claude will apply your full style guide: cold open, camera flip, data formula (source → number → implication), contrarian position, and the 9-point Mirko Lalli test. Output is editable before export.
                </p>
              </div>

              {/* Include Brief toggle */}
              <button
                data-testid="include-brief-toggle"
                onClick={() => setIncludeBrief(v => !v)}
                className={`flex items-center gap-3 w-full rounded-xl p-4 text-left transition-all border-2 ${
                  includeBrief
                    ? (dm ? "border-cyan-400/60 bg-cyan-400/8" : "border-[#006874]/40 bg-[#f0f9fa]")
                    : (dm ? "border-transparent bg-[#1a2122] hover:bg-[#1e2627]" : "border-transparent bg-[#f3f4f5] hover:bg-[#ecedef]")
                }`}
              >
                <div className={`w-9 h-5 rounded-full transition-all relative flex-shrink-0 ${includeBrief ? (dm ? "bg-cyan-400" : "bg-[#006874]") : (dm ? "bg-slate-700" : "bg-slate-300")}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${includeBrief ? "left-4" : "left-0.5"}`} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Prepend Brief of the Week</p>
                  <p className={`text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Include the current published brief at the top of the newsletter, before the selected articles</p>
                </div>
              </button>

              <button
                onClick={handleGenerate}
                className={`w-full py-3 rounded-full category-tag transition-all flex items-center justify-center gap-2 ${dm ? "bg-violet-400 text-white hover:bg-violet-300" : "bg-violet-600 text-white hover:bg-violet-700"}`}
              >
                <Brain className="w-3.5 h-3.5" />
                Generate with Claude
              </button>
            </div>
          )}

          {/* ── STEP: GENERATING ── */}
          {step === "generating" && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${dm ? "bg-violet-400/10" : "bg-violet-50"}`}>
                <Brain className={`w-8 h-8 ${dm ? "text-violet-400" : "text-violet-600"} animate-pulse`} />
              </div>
              <p className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Claude is writing…</p>
              <p className={`category-tag text-center max-w-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                Applying your style guide. Cold open, camera flip, data formula. This takes 15–30 seconds.
              </p>
              <div className="flex gap-1 mt-2">
                {[0,1,2].map(i => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full ${dm ? "bg-violet-400" : "bg-violet-600"} animate-bounce`} style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          )}

          {/* ── STEP: PREVIEW ── */}
          {step === "preview" && (
            <div className="flex flex-col gap-4 py-2">
              <div className="flex items-center justify-between">
                <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                  Draft ready — edit before exporting
                </p>
                <button onClick={reset} className={`category-tag transition-colors ${dm ? "text-slate-500 hover:text-slate-300" : "text-[#bac9cc] hover:text-[#6b7a7d]"}`}>
                  ← Regenerate
                </button>
              </div>

              <textarea
                value={editableNewsletter}
                onChange={(e) => setEditableNewsletter(e.target.value)}
                data-testid="newsletter-editor"
                className={`w-full min-h-[360px] p-4 rounded-xl text-sm font-mono leading-relaxed resize-y outline-none border-0 ${dm ? "bg-[#1a2122] text-slate-200 focus:ring-1 focus:ring-violet-400/30" : "bg-[#f3f4f5] text-[#191c1d] focus:ring-1 focus:ring-violet-600/20"}`}
              />

              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={handleCopy} className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all ${dm ? "bg-[#1a2122] text-slate-300 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}>
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copied!" : "Copy"}
                </button>
                <button onClick={handleDownloadMd} className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all ${dm ? "bg-[#1a2122] text-slate-300 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}>
                  <Download className="w-3 h-3" />
                  .md
                </button>
                <button onClick={handleDownloadHtml} className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all ${dm ? "bg-[#1a2122] text-slate-300 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}>
                  <Download className="w-3 h-3" />
                  .html
                </button>
                <div className="flex-1" />
                <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  {editableNewsletter.split(/\s+/).filter(Boolean).length} words
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
